import React, { useCallback, useEffect, useRef, useState } from "react";
import { Video } from "lucide-react";

const FONT = "'Space Grotesk', sans-serif";

/**
 * ZoomLinkModal — "connect this call" prompt for TseDashboard5's Zoom
 * integration. The agent pastes the Zoom meeting link or meeting ID they
 * were just given after starting the call on Zoom's own site, and we bind
 * it to this session so live transcript lines know which dashboard tab to
 * reach.
 *
 * Validates that the value is a recognisable Zoom URL or a 9-11 digit
 * meeting ID before allowing submit. Focus-trapped, accessible, and
 * guarded against double-submit.
 *
 * Props:
 *   onConfirm  (value: string) => void   — called with the trimmed input
 *   onCancel   () => void
 */

/* ── validation ────────────────────────────────────────────────────────── */

// Zoom URL: optional scheme, any *.zoom.us subdomain, /j/<digits>, optional query
const ZOOM_URL_RE = /^(https?:\/\/)?[\w-]*(\.)?zoom\.us\/j\/\d{9,11}(\?[^\s]*)?$/i;

// Bare meeting ID: 9-11 digits with optional spaces or dashes between groups
const MEETING_ID_RE = /^\d[\d\s-]{7,13}\d$/;

/** Strip non-digits to check the actual digit count of a bare ID. */
const isValidMeetingId = (s) => {
  if (!MEETING_ID_RE.test(s)) return false;
  const digits = s.replace(/[\s-]/g, "");
  return digits.length >= 9 && digits.length <= 11;
};

const isValidZoomInput = (s) => ZOOM_URL_RE.test(s) || isValidMeetingId(s);

/* ── focus trap ────────────────────────────────────────────────────────── */

/** Keep Tab cycling within the dialog panel. */
const trapFocus = (e, panelRef) => {
  if (e.key !== "Tab") return;
  const focusable = panelRef.current?.querySelectorAll(
    'input, button, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable?.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
};

/* ── component ─────────────────────────────────────────────────────────── */

const ZoomLinkModal = ({ onConfirm, onCancel }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  /** Element that was focused before the dialog opened — restored on close. */
  const returnFocusRef = useRef(null);

  const trimmed = value.trim();

  /* ── lifecycle: autofocus, Escape, focus restore ── */

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    inputRef.current?.focus();
    return () => returnFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      trapFocus(e, panelRef);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  /* ── handlers ── */

  const submit = useCallback(async () => {
    if (!trimmed || pending) return;

    if (!isValidZoomInput(trimmed)) {
      setError("Enter a valid Zoom link or 9-11 digit meeting ID.");
      return;
    }

    setPending(true);
    try {
      await onConfirm(trimmed);
    } catch {
      // onConfirm rejected — let the user retry
      setPending(false);
    }
  }, [trimmed, pending, onConfirm]);

  const handleBlur = () => {
    if (trimmed && !isValidZoomInput(trimmed)) {
      setError("Enter a valid Zoom link or 9-11 digit meeting ID.");
    } else {
      setError("");
    }
  };

  const handleChange = (e) => {
    setValue(e.target.value);
    // Clear error as soon as the user starts correcting
    if (error) setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  /* ── ids for aria wiring ── */
  const headingId = "zoom-modal-title";
  const descId = "zoom-modal-desc";
  const errorId = "zoom-modal-error";

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ fontFamily: FONT }}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onCancel}
      />

      {/* panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={descId}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden motion-safe:animate-[fadeIn_150ms_ease-out]"
      >
        {/* accent bar — thin top rule that ties the panel to the app's cyan palette */}
        <div className="h-1 w-full bg-gradient-to-r from-cyan-400 to-cyan-500" />

        <div className="px-6 pt-5 pb-6">
          {/* two-column layout: icon | content, replaces the old ml-12 hack */}
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Video size={17} className="text-cyan-500" strokeWidth={2} />
            </div>

            <div className="flex-1 min-w-0">
              <h2
                id={headingId}
                className="text-base font-semibold text-[#253439] leading-snug"
              >
                Connect this call
              </h2>

              <p
                id={descId}
                className="text-sm text-gray-500 leading-relaxed mt-1.5 mb-3"
              >
                Paste the Zoom meeting link or ID for the call you just
                started, so the live transcript can find its way here.
              </p>

              {/* input + helper text */}
              <div>
                <label htmlFor="zoom-link-input" className="sr-only">
                  Zoom meeting link or ID
                </label>
                <input
                  id="zoom-link-input"
                  ref={inputRef}
                  type="text"
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder="https://zoom.us/j/1234567890"
                  aria-invalid={!!error || undefined}
                  aria-describedby={error ? errorId : undefined}
                  disabled={pending}
                  className={[
                    "w-full px-3 py-2 text-sm border rounded-lg outline-none transition",
                    error
                      ? "border-red-400 focus:border-red-400 focus:ring-1 focus:ring-red-300"
                      : "border-gray-200 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400",
                    pending ? "opacity-60" : "",
                  ].join(" ")}
                />

                {error ? (
                  <p id={errorId} className="text-xs text-red-500 mt-1.5" role="alert">
                    {error}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1.5">
                    Or paste the meeting ID, e.g. 123 4567 8901
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onCancel}
              disabled={pending}
              className="px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 rounded-lg transition disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!trimmed || pending}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition bg-[#06b6d4] hover:bg-cyan-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pending ? "Connecting…" : "Connect"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoomLinkModal;
